import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Sparkles, CalendarHeart, ShieldCheck, Star, Map } from 'lucide-react';
import { CoachOnboarding } from '../../components/prenatal/CoachOnboarding';
import { WeekPlan } from '../../components/prenatal/WeekPlan';
import { ProgressJourney } from '../../components/prenatal/ProgressJourney';

export default function PrenatalCoach() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');

  const fetchStatus = useCallback(() => {
    api.get('/api/prenatal-coach/status')
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Paid-trial learners don't see the Coach on the web portal (founder,
  // 3 Oct 2026) — it was confusing before they enrol. Covers a bookmarked URL.
  useEffect(() => {
    if (user?.learner_type === 'trial_paid') navigate('/learner/dashboard', { replace: true });
  }, [user, navigate]);
  if (user?.learner_type === 'trial_paid') return null;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  // ── Postnatal: the video coach is retired — recovery runs on LIVE yoga classes ──
  if (status?.stage === 'postnatal') {
    return (
      <div className="max-w-2xl mx-auto pt-6" data-testid="coach-postnatal-live">
        <Card className="rounded-2xl overflow-hidden border-primary/20">
          <div className="bg-gradient-to-br from-primary/10 via-rose-50 to-amber-50/50 px-6 py-10 text-center">
            <div className="h-14 w-14 rounded-full bg-white/80 shadow-sm flex items-center justify-center mx-auto mb-4">
              <CalendarHeart className="h-7 w-7 text-primary" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Your recovery is guided live</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
              Now that your little one has arrived, your postnatal recovery is guided through <strong>live yoga classes</strong> with our instructors — not pre-recorded videos. SoulAI is still right here in chat whenever you have a question.
            </p>
            <Button className="mt-6" onClick={() => navigate('/learner/book')} data-testid="coach-postnatal-book-btn">
              Book a live class
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Coming Soon (default for everyone until admin enables / public launch) ──
  if (!status?.access_enabled || status?.access?.level === 'none') {
    return (
      <div className="max-w-2xl mx-auto pt-6" data-testid="coach-coming-soon">
        <Card className="rounded-2xl overflow-hidden border-primary/20">
          <div className="bg-gradient-to-br from-primary/10 via-rose-50 to-amber-50/50 px-6 py-10 text-center">
            <div className="h-14 w-14 rounded-full bg-white/80 shadow-sm flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">SoulAI Prenatal Coach</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
              Your personal AI-guided prenatal practice — weekly 15–20 minute video classes,
              safely tailored to your pregnancy week, health profile and how you feel.
            </p>
            <div className="inline-flex items-center gap-1.5 mt-5 px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium">
              Coming Soon
            </div>
          </div>
          <CardContent className="py-6">
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              <div className="space-y-1.5">
                <ShieldCheck className="h-5 w-5 text-primary mx-auto" />
                <p className="text-xs font-medium">Safety-first</p>
                <p className="text-[11px] text-muted-foreground">Every class is matched against your health conditions</p>
              </div>
              <div className="space-y-1.5">
                <CalendarHeart className="h-5 w-5 text-primary mx-auto" />
                <p className="text-xs font-medium">Week-by-week</p>
                <p className="text-[11px] text-muted-foreground">A fresh plan every week as your pregnancy progresses</p>
              </div>
              <div className="space-y-1.5">
                <Star className="h-5 w-5 text-primary mx-auto" />
                <p className="text-xs font-medium">Learns from you</p>
                <p className="text-[11px] text-muted-foreground">Rate each class and your plan adapts automatically</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!status.onboarded) {
    return <CoachOnboarding config={status.config} edd={status.edd} week={status.week} onDone={fetchStatus}
                            stage="prenatal" prefill={status.prefill} eddLocked={!!status.edd_locked} />;
  }

  const showJourneyToggle = !status.waiting_for_scan;

  return (
    <div className="space-y-4">
      {showJourneyToggle && (
        <div className="inline-flex rounded-full bg-muted p-1 gap-0.5" data-testid="coach-view-toggle">
          <button
            onClick={() => setView('week')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 flex items-center gap-1.5
              ${view === 'week' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="coach-view-week-btn">
            <Sparkles className="h-3.5 w-3.5" />This Week
          </button>
          <button
            onClick={() => setView('journey')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 flex items-center gap-1.5
              ${view === 'journey' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="coach-view-journey-btn">
            <Map className="h-3.5 w-3.5" />My Journey
          </button>
        </div>
      )}
      {view === 'journey' && showJourneyToggle
        ? <ProgressJourney />
        : <WeekPlan status={status} onStatusRefresh={fetchStatus} />}
    </div>
  );
}
